import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';

export interface CartAddon {
  id: string;
  name: string;
  name_de: string;
  price: number;
}

export interface CartItem {
  cartId: string; // unique key for this cart entry
  menuItemId: string;
  name: string;
  name_de: string;
  price: number; // resolved per-unit price (includes size base + modifier deltas, NOT addons)
  quantity: number;
  categoryName: string;
  imageUrl: string;
  notes: string;
  addons: CartAddon[];
  // Domino's-style modifier selections
  // selections: groupId → [optionId]; modifier_snapshot: full group+option data captured at add time
  selections?: Record<string, string[]>;
  modifier_snapshot?: Array<{
    id: string;
    name: string;
    name_de?: string;
    options: Array<{ id: string; name: string; name_de?: string; is_default?: boolean }>;
  }>;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'cartId'>) => void;
  removeItem: (cartId: string) => void;
  updateQuantity: (cartId: string, quantity: number) => void;
  clearCart: () => void;
  total: number;
  itemCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function generateCartId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
}

const CART_KEY = 'qr_cart_v2';

// Generate a unique ID for this browser tab to detect own writes
const TAB_ID = Date.now().toString(36) + Math.random().toString(36).substring(2, 7);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(CART_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Debounce timer for writing to Firestore
  const writeTimer = useRef<NodeJS.Timeout | null>(null);
  const lastWriteTabId = useRef<string>('');
  const isListenerReady = useRef(false);

  // Save to localStorage always (offline fallback)
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  // Write cart to Firestore (debounced to avoid rapid writes)
  const writeToFirestore = useCallback((cartItems: CartItem[]) => {
    if (!user) return;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      lastWriteTabId.current = TAB_ID;
      const cartRef = doc(db, 'customer_carts', user.uid);
      setDoc(cartRef, {
        items: cartItems,
        updated_at: Date.now(),
        writer_tab: TAB_ID,
      }, { merge: true }).catch(() => {});
    }, 300);
  }, [user]);

  // Sync items TO Firestore when they change locally
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
    if (!user || !isListenerReady.current) return;
    writeToFirestore(items);
  }, [items, user, writeToFirestore]);

  // Listen to Firestore cart changes (real-time sync across devices)
  useEffect(() => {
    if (!user) {
      isListenerReady.current = false;
      return;
    }

    const cartRef = doc(db, 'customer_carts', user.uid);
    let isFirstSnapshot = true;

    const unsub = onSnapshot(cartRef, (snap) => {
      // Get current localStorage cart (the cart the user built before login)
      let localCart: CartItem[] = [];
      try {
        const stored = localStorage.getItem(CART_KEY);
        if (stored) localCart = JSON.parse(stored);
      } catch { /* ignore */ }

      if (!snap.exists()) {
        // No remote cart — KEEP local cart (don't wipe it!)
        // This is the critical fix: user built a cart, then registered → keep their cart
        if (isFirstSnapshot && localCart.length > 0) {
          console.log('[CART] No remote cart, keeping local cart with', localCart.length, 'items');
          setItems(localCart);
          // Push local cart to Firestore so it syncs
          writeToFirestore(localCart);
        }
        isListenerReady.current = true;
        isFirstSnapshot = false;
        return;
      }

      const data = snap.data();
      const remoteItems: CartItem[] = data.items || [];
      const writerTab: string = data.writer_tab || '';

      if (isFirstSnapshot) {
        // On first load after login: merge local + remote, prefer whichever has items
        if (remoteItems.length > 0) {
          // Remote has items — use remote (returning customer with saved cart)
          setItems(remoteItems);
        } else if (localCart.length > 0) {
          // Remote is empty but local has items — keep local (just registered/logged in)
          console.log('[CART] Remote cart empty, keeping local cart with', localCart.length, 'items');
          setItems(localCart);
          writeToFirestore(localCart);
        }
        // If both empty, items stay empty (default)
        isFirstSnapshot = false;
        isListenerReady.current = true;
        return;
      }

      // Subsequent snapshots: only apply if change came from a DIFFERENT tab
      if (writerTab !== TAB_ID) {
        setItems(remoteItems);
      }
      isListenerReady.current = true;
    });

    return () => {
      unsub();
      isListenerReady.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const addItem = useCallback((item: Omit<CartItem, 'cartId'>) => {
    setItems((prev) => [...prev, { ...item, cartId: generateCartId() }]);
  }, []);

  const removeItem = useCallback((cartId: string) => {
    setItems((prev) => prev.filter((i) => i.cartId !== cartId));
  }, []);

  const updateQuantity = useCallback((cartId: string, quantity: number) => {
    if (quantity < 1) return;
    setItems((prev) =>
      prev.map((i) => (i.cartId === cartId ? { ...i, quantity } : i))
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_KEY);
  }, []);

  const total = items.reduce((sum, item) => {
    const addonTotal = item.addons.reduce((a, addon) => a + addon.price, 0);
    return sum + (item.price + addonTotal) * item.quantity;
  }, 0);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQuantity, clearCart, total, itemCount }}
    >
      {children}
    </CartContext.Provider>
  );
};

export function useCart(): CartContextType {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
